import React, { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import PopoverMenu from "./PopoverMenu"
import ProfileCard from "./ProfileCard"
import { RequestReferralModal } from "../../shared/components/RequestReferral/RequestReferralModal"
import {
  selectCurrentUserRoles,
  UserRoles,
} from "../../../domain/Account/userSlice"

import { ContactDisplayIdTypes } from "../../../domain/CMS/schema"
import { PublicLocation, PublicPractice } from "../../../domain/Profile/schema"
import {
  LiabilityInsurance,
  ProviderDisclosure,
} from "../../../domain/Provider/schema"
import { getLocationLiabilityStatus } from "../utils/liabilityStatusHelpers"
import {
  getAggregateInsuranceStatus,
  LiabilityStatusEnum,
} from "../../shared/utils"

import {
  PracticeProfileSubSectionIds,
  ProviderProfileSubSectionIds,
} from "../constants"
import { ProviderTypeDisplayId } from "../../../domain/enums/displayId.enum"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import ProfileBanners from "./ProfileBanners"
import { scrollToElement } from "../utils/scrollToElement"
import { type BannerType } from "../../shared/components/Banner"
import Button from "../../shared/components/Button"
import useContentLayoutContext from "../../layout/useContentLayoutContext"
import { LocationTypeDisplayId } from "../../../domain/Practice/schema"

interface ProfileSectionItem {
  id: string
  label: string
}

interface referralProviderInfo {
  providerName: string
  providerId: string
  isUnderSupervision: boolean
  status?: string
  providerTypeDisplayId?: string
}

interface referralPracticeInfo {
  practiceName: string
  practiceId: string
  status?: string
}

interface referralDetails {
  location: PublicLocation
  practiceInfo: referralPracticeInfo
  providerInfo: referralProviderInfo
  practice: PublicPractice
  hasProviderLicense: boolean
  providerLiabilityInsurances?: LiabilityInsurance[]
  providerDisclosures?: ProviderDisclosure[]
}

interface ProfileHeaderProps {
  isPublicViewable: boolean
  phoneNumberList: {
    id: string
    number: string
    phoneTypeDisplayId: ContactDisplayIdTypes | null
    isPrimary: boolean
  }[]
  photoFileId?: string
  primaryEmailAddress: string
  profileId: string
  profileName: string
  profileSections: ProfileSectionItem[]
  profileTagDisplayIds?: string[]
  profileType: "providerProfile" | "practiceProfile"
  providerDesignationDisplayIds?: string[]
  referralDetails?: referralDetails[]
  websiteUrl?: string
  practiceStatus?: string
  hasProviders?: boolean
  isManaged?: boolean
  isRegistered?: boolean
  providerStatusDisplayId?: string
  // Banner-related props (optional for backwards compatibility)
  bannerData?: {
    showLicenseBanner: boolean
    licenseText?: string
    showLiabilityBanner: boolean
    liabilityText?: string
    liabilityClickable?: boolean
    liabilityType?: BannerType
    showDisclosureBanner: boolean
    disclosureText?: string
    disclosureClickable?: boolean
    showPracticeAgreementBanner?: boolean
    practiceAgreementText?: string
  }
}

interface ContactProps {
  primaryEmailAddress: string
  profileSections: ProfileSectionItem[]
  profileType: "providerProfile" | "practiceProfile"
  referralDetails?: referralDetails[]
  practiceStatus?: string
  hasProviders?: boolean
}

const Contact: React.FC<ContactProps> = ({
  primaryEmailAddress,
  profileSections,
  profileType,
  referralDetails,
  practiceStatus,
  hasProviders,
}) => {
  const [isRequestReferralModalOpen, setIsRequestReferralModalOpen] =
    useState(false)
  const { scrollableContainerRef } = useContentLayoutContext()
  const {
    cms: { publicProfileSlide: content, common: commonTexts },
  } = useCMSReady(["common", "publicProfileSlide"])
  const handleMessageButtonClick = () => {
    if (primaryEmailAddress) {
      window.open(`mailto:${primaryEmailAddress}`, "_self")
    }
  }

  const userRoles = useSelector(selectCurrentUserRoles)

  const canRequestReferral = [
    UserRoles.ROLE_AFFILIATE_RELATIONS,
    UserRoles.ROLE_EAP_RESEARCH,
  ].some((role) => userRoles.includes(role))

  const handleReferralOptionsButtonClick = () => {
    if (profileType === "practiceProfile") {
      scrollToElement(
        PracticeProfileSubSectionIds.PRACTICE_PROFILE_THERAPIST_SECTION,
        scrollableContainerRef,
      )
    } else if (referralDetails && referralDetails.length > 1) {
      scrollToElement(
        ProviderProfileSubSectionIds.PROVIDER_PROFILE_LOCATIONS_SECTION,
        scrollableContainerRef,
      )
    } else if (referralDetails?.length === 1) {
      setIsRequestReferralModalOpen(true)
    }
  }

  const selectedReferral =
    referralDetails?.length === 1 ? referralDetails[0] : null

  const shouldDisplayReferralOptionsButton = useMemo(() => {
    if (!canRequestReferral) return false

    if (profileType === "providerProfile") {
      if (!Array.isArray(referralDetails)) return false

      const hasActivePractice = referralDetails.some(
        (ref) => ref.practiceInfo.status === "active",
      )
      const hasActiveProvider = referralDetails.some(
        (ref) => ref.providerInfo.status === "active",
      )

      return hasActivePractice && hasActiveProvider
    }

    // For practiceProfile
    return practiceStatus === "active" && hasProviders
  }, [
    canRequestReferral,
    profileType,
    referralDetails,
    practiceStatus,
    hasProviders,
  ])

  return (
    <div className="flex items-center justify-between w-full mb-5">
      <div className={"flex items-center flex-grow space-x-6 mr-6"}>
        <Button
          buttonLabel={content?.messageButtonLabel}
          onClick={handleMessageButtonClick}
          type="transparent"
          roundedFull={false}
          rootStyleOverrides="w-1/2"
        />
        {shouldDisplayReferralOptionsButton && (
          <Button
            buttonLabel={content?.referralOptionsButtonLabel}
            onClick={handleReferralOptionsButtonClick}
            roundedFull={false}
            rootStyleOverrides="w-1/2 !ml-[26px] !mr-[6px]"
          />
        )}
        <PopoverMenu
          items={profileSections}
          scrollableContainerRef={scrollableContainerRef}
        />
      </div>
      {isRequestReferralModalOpen && selectedReferral && (
        <RequestReferralModal
          isOpen={isRequestReferralModalOpen}
          onClose={() => setIsRequestReferralModalOpen(false)}
          providerId={selectedReferral.providerInfo.providerId}
          practiceId={selectedReferral.practiceInfo.practiceId}
          isUnderSupervision={selectedReferral.providerInfo.isUnderSupervision}
          locationData={{
            locationId: selectedReferral.location.id || null,
            provider: selectedReferral.providerInfo.providerName,
            practice: selectedReferral.practiceInfo.practiceName,
            careType:
              selectedReferral.location.locationTypeDisplayId ===
              LocationTypeDisplayId.VIRTUAL
                ? (commonTexts?.virtual ?? "")
                : (commonTexts?.inPerson ?? ""),
            officeLocation:
              selectedReferral.location.locationTypeDisplayId ===
              LocationTypeDisplayId.VIRTUAL
                ? (commonTexts?.virtual ?? "")
                : `${selectedReferral.location.line1} ${selectedReferral.location.line2}, ${selectedReferral.location.city}, ${selectedReferral.location.stateOrTerritory} ${selectedReferral.location.postalCode}`,
          }}
          hasPracticeLiabilityInsurance={
            getLocationLiabilityStatus(
              selectedReferral.practice,
              selectedReferral.providerLiabilityInsurances,
            ).hasActiveLiability
          }
          hasProviderLiabilityInsurance={[
            LiabilityStatusEnum.ACTIVE,
            LiabilityStatusEnum.EXPIRING_SOON,
          ].includes(
            getAggregateInsuranceStatus(
              selectedReferral.providerLiabilityInsurances || [],
            ),
          )}
          hasProviderLicense={selectedReferral.hasProviderLicense}
          hasProviderDisclosure={
            (selectedReferral.providerDisclosures || []).some(
              (disclosure) => disclosure.fileId && disclosure.envelopeId,
            ) ||
            selectedReferral.providerInfo.providerTypeDisplayId ===
              ProviderTypeDisplayId.MANAGED
          }
        />
      )}
    </div>
  )
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  isPublicViewable,
  providerDesignationDisplayIds,
  photoFileId,
  phoneNumberList,
  primaryEmailAddress,
  profileId,
  profileName,
  profileSections,
  profileTagDisplayIds,
  profileType,
  referralDetails,
  websiteUrl,
  practiceStatus,
  hasProviders = false,
  isManaged = false,
  isRegistered,
  providerStatusDisplayId,
  // Banner-related props
  bannerData,
}) => {
  const isPrivate = !isPublicViewable

  return (
    <div className="flex flex-col gap-5 sticky top-0 bg-white z-20 mx-6">
      <ProfileBanners
        profileType={profileType}
        bannerData={bannerData}
        isPrivate={isPrivate}
      />
      <ProfileCard
        phoneNumberList={phoneNumberList}
        photoFileId={photoFileId}
        profileId={profileId}
        profileName={profileName}
        profileTagDisplayIds={profileTagDisplayIds}
        profileType={profileType}
        providerDesignationDisplayIds={providerDesignationDisplayIds}
        websiteUrl={websiteUrl}
        isManaged={isManaged}
        isRegistered={isRegistered}
        status={practiceStatus}
        providerStatusDisplayId={providerStatusDisplayId}
      />
      <Contact
        referralDetails={referralDetails}
        primaryEmailAddress={primaryEmailAddress}
        profileSections={profileSections}
        profileType={profileType}
        practiceStatus={practiceStatus}
        hasProviders={hasProviders}
      />
    </div>
  )
}

export default ProfileHeader
